import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
    TemplateSection, Indication, SupportedLocale, I18nString,
} from '../../../services/apiService';
import { pickLocale } from '../../../utils/i18n';
import ToothChart from './ToothChart';

// -----------------------------------------------------------------------------
// Shared utilities
// -----------------------------------------------------------------------------

const cardClass = 'rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5';
const labelClass = 'text-[11px] font-medium uppercase tracking-wider text-brand-text-muted';

interface SectionCardProps {
    label: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ label, children, actions }) => (
    <div className={cardClass}>
        <div className="flex items-center justify-between mb-3 gap-3">
            <h3 className={labelClass}>{label}</h3>
            {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
        {children}
    </div>
);

function resolvePlaceholder(cfgPlaceholder: unknown, locale: SupportedLocale): string {
    if (!cfgPlaceholder) return '';
    if (typeof cfgPlaceholder === 'string') return cfgPlaceholder;
    if (typeof cfgPlaceholder === 'object') return pickLocale(cfgPlaceholder as I18nString, locale);
    return '';
}

// -----------------------------------------------------------------------------
// single_choice — Pillen, genau eine auswaehlbar
// -----------------------------------------------------------------------------

interface SingleChoiceProps {
    section: TemplateSection;
    value: string | null | undefined;
    onChange: (next: string | null) => void;
    locale: SupportedLocale;
}

const SingleChoiceSection: React.FC<SingleChoiceProps> = ({ section, value, onChange, locale }) => (
    <SectionCard label={pickLocale(section.label, locale)}>
        <div className="flex flex-wrap gap-2">
            {(section.options || []).map(opt => {
                const selected = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(selected ? null : opt.value)}
                        className={
                            selected
                                ? 'px-4 py-2 rounded-md text-sm font-medium bg-brand-primary text-white border border-brand-primary cursor-pointer transition-colors'
                                : 'px-4 py-2 rounded-md text-sm font-medium bg-brand-surface text-brand-text border border-brand-border hover:bg-brand-surface-2 cursor-pointer transition-colors'
                        }
                        aria-pressed={selected}
                    >
                        {pickLocale(opt.label, locale)}
                    </button>
                );
            })}
        </div>
    </SectionCard>
);

// -----------------------------------------------------------------------------
// multi_choice — Buttons, beliebig viele auswaehlbar
// -----------------------------------------------------------------------------

interface MultiChoiceProps {
    section: TemplateSection;
    value: string[] | undefined;
    onChange: (next: string[]) => void;
    locale: SupportedLocale;
}

const MultiChoiceSection: React.FC<MultiChoiceProps> = ({ section, value, onChange, locale }) => {
    const selected = new Set(value || []);
    const toggle = (v: string) => {
        const next = new Set(selected);
        if (next.has(v)) next.delete(v); else next.add(v);
        onChange(Array.from(next));
    };
    return (
        <SectionCard label={pickLocale(section.label, locale)}>
            <div className="flex flex-wrap gap-2">
                {(section.options || []).map(opt => {
                    const active = selected.has(opt.value);
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggle(opt.value)}
                            className={
                                active
                                    ? 'px-4 py-2 rounded-md text-sm font-medium bg-brand-primary text-white border border-brand-primary cursor-pointer transition-colors'
                                    : 'px-4 py-2 rounded-md text-sm font-medium bg-brand-surface text-brand-text border border-brand-border hover:bg-brand-surface-2 cursor-pointer transition-colors'
                            }
                            aria-pressed={active}
                        >
                            {pickLocale(opt.label, locale)}
                        </button>
                    );
                })}
            </div>
        </SectionCard>
    );
};

// -----------------------------------------------------------------------------
// tooth_chart — FDI mit optionalem Milchzahn-Toggle (im ToothChart drin)
// -----------------------------------------------------------------------------

interface ToothChartProps {
    section: TemplateSection;
    value: string[] | undefined;
    onChange: (next: string[]) => void;
    locale: SupportedLocale;
}

const ToothChartSection: React.FC<ToothChartProps> = ({ section, value, onChange, locale }) => {
    const cfg = (section.config || {}) as { allow_deciduous?: boolean };
    return (
        <SectionCard
            label={pickLocale(section.label, locale)}
            actions={
                <span className="text-[11px] text-brand-text-muted tabular-nums">
                    {(value || []).length}/32
                </span>
            }
        >
            <ToothChart
                value={value || []}
                onChange={onChange}
                allowDeciduous={cfg.allow_deciduous === true}
            />
        </SectionCard>
    );
};

// -----------------------------------------------------------------------------
// text_freeform — Textarea mit i18n-Placeholder
// -----------------------------------------------------------------------------

interface TextFreeformProps {
    section: TemplateSection;
    value: string | undefined;
    onChange: (next: string) => void;
    locale: SupportedLocale;
}

const TextFreeformSection: React.FC<TextFreeformProps> = ({ section, value, onChange, locale }) => {
    const cfg = (section.config || {}) as { placeholder?: string | I18nString };
    const placeholder = resolvePlaceholder(cfg.placeholder, locale);
    return (
        <SectionCard label={pickLocale(section.label, locale)}>
            <textarea
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                rows={3}
                className="w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors resize-y"
            />
        </SectionCard>
    );
};

// -----------------------------------------------------------------------------
// chips_indication — Tag-Multi-Select gegen globales Indikations-Vokabular
// -----------------------------------------------------------------------------

interface ChipsIndicationProps {
    section: TemplateSection;
    value: string[] | undefined;
    onChange: (next: string[]) => void;
    locale: SupportedLocale;
    indications: Indication[];
}

const ChipsIndicationSection: React.FC<ChipsIndicationProps> = ({ section, value, onChange, locale, indications }) => {
    const { t } = useTranslation();
    const selected = new Set(value || []);
    const toggle = (v: string) => {
        const next = new Set(selected);
        if (next.has(v)) next.delete(v); else next.add(v);
        onChange(Array.from(next));
    };
    return (
        <SectionCard
            label={pickLocale(section.label, locale)}
            actions={
                selected.size > 0 ? (
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="text-[11px] text-brand-text-muted hover:text-brand-text cursor-pointer transition-colors"
                    >
                        {t('notes.reset')}
                    </button>
                ) : undefined
            }
        >
            <div className="flex flex-wrap gap-2">
                {indications.map(ind => {
                    const active = selected.has(ind.value);
                    return (
                        <button
                            key={ind.value}
                            type="button"
                            onClick={() => toggle(ind.value)}
                            className={
                                active
                                    ? 'inline-flex items-center gap-1 pl-3 pr-2 py-1.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary cursor-pointer transition-colors'
                                    : 'px-3 py-1.5 rounded-full text-xs font-medium bg-brand-surface text-brand-text-muted border border-brand-border hover:bg-brand-surface-2 hover:text-brand-text cursor-pointer transition-colors'
                            }
                            aria-pressed={active}
                        >
                            {pickLocale(ind.label, locale)}
                            {active && <X className="w-3 h-3" aria-hidden />}
                        </button>
                    );
                })}
            </div>
        </SectionCard>
    );
};

// -----------------------------------------------------------------------------
// Dispatcher — wuerde sonst in jedem Konsumenten dupliziert
// -----------------------------------------------------------------------------

interface SectionInputProps {
    section: TemplateSection;
    value: any;
    onChange: (next: any) => void;
    locale: SupportedLocale;
    indications: Indication[];
}

export const SectionInput: React.FC<SectionInputProps> = ({ section, value, onChange, locale, indications }) => {
    const { t } = useTranslation();
    switch (section.type) {
        case 'single_choice':
            return <SingleChoiceSection section={section} value={value as string | null | undefined} onChange={onChange} locale={locale} />;
        case 'multi_choice':
            return <MultiChoiceSection section={section} value={value as string[] | undefined} onChange={onChange} locale={locale} />;
        case 'tooth_chart':
            return <ToothChartSection section={section} value={value as string[] | undefined} onChange={onChange} locale={locale} />;
        case 'text_freeform':
            return <TextFreeformSection section={section} value={value as string | undefined} onChange={onChange} locale={locale} />;
        case 'chips_indication':
            return <ChipsIndicationSection section={section} value={value as string[] | undefined} onChange={onChange} locale={locale} indications={indications} />;
        default:
            return (
                <SectionCard label={pickLocale(section.label, locale)}>
                    <p className="text-xs text-brand-text-muted">
                        {t('notes.unknownSectionType', { type: (section as any).type })}
                    </p>
                </SectionCard>
            );
    }
};

export {
    SingleChoiceSection, MultiChoiceSection, ToothChartSection,
    TextFreeformSection, ChipsIndicationSection, SectionCard,
};
